import { create } from "zustand";
import type { RefineEvent, SelectionAction } from "~/features/documents/editor/selectionTypes";

/**
 * The results drawer's VIEW state — one action in flight, its thoughts, its result, and the
 * refine conversation that may follow.
 *
 * Deliberately not a store class and deliberately not React Query: the catalogue is server
 * state (`useScopedAiActions`) and the display preference is per-viewer localStorage
 * (`aiActionPreferences`, W1). What is left is this — which drawer is open over which range,
 * and it belongs to the view.
 *
 * ⚠ It lives in the PACKAGE rather than in slate because the drawer does: `packages/docit-docs`
 * is consumed by more than one host, and a component reaching up into one host's `src/` would
 * build in that host and nowhere else. `slate/src/features/documents/useSelectionActions.ts`
 * re-exports it, so the path the W6 spec names still resolves.
 */

export type DrawerPhase = "idle" | "processing" | "result" | "conversation" | "error" | "gated";

export interface DrawerBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface ConversationTurn {
  role: "user" | "ai";
  content: string;
}

interface SelectionActionsState {
  phase: DrawerPhase;
  /** The action being run — its title is the drawer's own title. */
  action: SelectionAction | null;
  /** The selection this run is anchored to, in ProseMirror positions. */
  range: { from: number; to: number } | null;
  /** The text as it was when the run started; every refine re-sends it. */
  originalText: string;
  /** Viewport rect of the selection, for positioning. Null ⇒ the drawer centres itself. */
  bounds: DrawerBounds | null;
  /** The model's reasoning, in arrival order. Rendered per the viewer's display preference. */
  thoughts: string[];
  /** The current candidate replacement. */
  result: string;
  /** The refine conversation, oldest first. */
  turns: ConversationTurn[];
  /** The partial bubble while a refinement streams. */
  streaming: string;
  error: string | null;
  /** Set when the provider gate reports an explicitly unconfigured provider. */
  gateReason: string | null;

  open(input: {
    action: SelectionAction;
    range: { from: number; to: number };
    originalText: string;
    bounds: DrawerBounds | null;
  }): void;
  applyEvent(event: RefineEvent): void;
  addThought(thought: string): void;
  setResult(result: string): void;
  fail(message: string): void;
  gate(reason: string): void;
  startConversation(): void;
  addTurn(turn: ConversationTurn): void;
  beginStreaming(): void;
  close(): void;
}

const EMPTY = {
  phase: "idle" as DrawerPhase,
  action: null,
  range: null,
  originalText: "",
  bounds: null,
  thoughts: [] as string[],
  result: "",
  turns: [] as ConversationTurn[],
  streaming: "",
  error: null,
  gateReason: null,
};

export const useSelectionActionsStore = create<SelectionActionsState>()((set) => ({
  ...EMPTY,

  open: ({ action, range, originalText, bounds }) =>
    set({ ...EMPTY, phase: "processing", action, range, originalText, bounds }),

  /**
   * One place turns a stream frame into drawer state, so the drawer and the refine call cannot
   * disagree about what a `delta` means. `delta` appends to the streaming bubble; `result`
   * replaces the candidate and ends the bubble.
   */
  applyEvent: (event) =>
    set((state) => {
      if (event.type === "thought") {
        return { thoughts: [...state.thoughts, event.text] };
      }
      if (event.type === "delta") {
        return { streaming: state.streaming + event.text };
      }
      if (event.type === "result") {
        return {
          result: event.text,
          streaming: "",
          turns: [...state.turns, { role: "ai" as const, content: event.text }],
        };
      }
      return { phase: "error" as DrawerPhase, error: event.message, streaming: "" };
    }),

  addThought: (thought) => set((state) => ({ thoughts: [...state.thoughts, thought] })),
  setResult: (result) => set({ phase: "result", result, streaming: "" }),
  fail: (message) => set({ phase: "error", error: message, streaming: "" }),
  gate: (reason) => set({ phase: "gated", gateReason: reason }),
  startConversation: () => set({ phase: "conversation" }),
  addTurn: (turn) => set((state) => ({ turns: [...state.turns, turn] })),
  beginStreaming: () => set({ streaming: "" }),
  close: () => set({ ...EMPTY }),
}));

/**
 * How many thoughts to render, per the viewer's preference. `verbose` shows everything,
 * `summary` the last three (the default), `minimal` the last one, `none` hides the section —
 * which is a different thing from having no thoughts.
 */
export function visibleThoughts(
  thoughts: string[],
  mode: "verbose" | "summary" | "minimal" | "none",
): string[] {
  if (mode === "none") {
    return [];
  }
  if (mode === "verbose") {
    return thoughts;
  }
  return thoughts.slice(mode === "summary" ? -3 : -1);
}
